import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { setWritesAllowed } from '../../../api';
import { StepId, STEP_ORDER } from '../../constants';
import { WizardHeaderProvider } from '../../context/wizard-header.context';
import { StepTransition } from '../motion/step-transition';
import { WizardHeaderCard } from './wizard-header-card';

const stepFromPath = (pathname: string): StepId | undefined => {
  const last = pathname.split('/').filter(Boolean).pop();
  if (last == null) return undefined;
  return (STEP_ORDER as readonly string[]).includes(last)
    ? (last as StepId)
    : undefined;
};

export const WizardShell = (): JSX.Element => {
  const location = useLocation();

  // Stage gate: writes are allowed only while the user is on the Review step.
  // Cleanup re-locks on unmount so navigating away from the wizard can never
  // leave a permissive global flag behind.
  useEffect(() => {
    const current = stepFromPath(location.pathname);
    setWritesAllowed(current === 'review');
    return (): void => setWritesAllowed(false);
  }, [location.pathname]);

  return (
    <WizardHeaderProvider>
      <HHStack sx={{ height: '100%', minHeight: 0 }}>
        <HHStack sx={{ px: 2 }}>
          <HHTypography hhVariant="section-title">
            Contract Onboarding
          </HHTypography>
        </HHStack>
        <WizardHeaderCard />
        <HHStack
          flex={1}
          sx={{
            minWidth: 0,
            p: 2,
            overflow: 'auto',
            // Make the page itself a column-flex child that fills the scroll
            // viewport. Combined with `mt: 'auto'` on StepNav, this anchors the
            // action buttons to the bottom on every step regardless of content
            // length — without having to modify each page file.
            '& > *:first-of-type': {
              flex: 1,
              minHeight: '100%',
            },
          }}
        >
          {/* Re-keyed on the path so the entrance keyframe replays on every
              step change. StepTransition forwards the flex/stretch above to the
              page inside it, so StepNav stays bottom-anchored. */}
          <StepTransition key={location.pathname}>
            <Outlet />
          </StepTransition>
        </HHStack>
      </HHStack>
    </WizardHeaderProvider>
  );
};
